import React from 'react';

import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@pkg/hooks';

import { useNavigationStore } from '@app/store/zustand';

import { NavButton } from '../../NavButton';
import { Toggle } from '../../Toggle';
import { collapseTransition, groupAnimation, itemAnimation } from '../../animations';
import { Buttons, Collapse, Collapseable } from '../../styles';

interface SectionProps {
    isOpen: boolean;
    setIsOpen?: (isOpen: boolean) => void;
    isStatic?: boolean;
    isMinimized?: boolean;
}

export const ProfileSection = ({ isOpen, setIsOpen, isStatic, isMinimized }: SectionProps) => {
    const location = useLocation();
    const toggleMobileMenu = useNavigationStore((state) => state.toggleMobileMenu);
    const primaryYat = useAppSelector((state) => state.user?.primaryYat);

    const { yatsPortfolio, marketplaceV1Enabled } = useAppSelector((state) => state.featureFlags.features);

    const profilePath = primaryYat ? `${primaryYat}` : 'profile';

    return (
        <>
            <Collapseable>
                <Toggle
                    icon="profile"
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    isStatic={isStatic}
                    aria-expanded={isOpen}
                    aria-controls="sidenav-section-profile"
                    titleLink={`/${profilePath}`}
                >
                    Profile
                </Toggle>

                <Collapse
                    variants={groupAnimation}
                    initial={false}
                    animate={isOpen ? 'open' : 'closed'}
                    transition={collapseTransition}
                    aria-labelledby="sidenav-section-profile"
                    $allowOverflow={isMinimized}
                >
                    <Buttons>
                        <NavButton
                            to={`/${profilePath}`}
                            variants={itemAnimation}
                            isActive={location.pathname.endsWith(`/${encodeURIComponent(profilePath)}`)}
                            onClick={() => toggleMobileMenu(false)}
                        >
                            NFTs
                        </NavButton>

                        <NavButton
                            to={`/${profilePath}/links`}
                            variants={itemAnimation}
                            isActive={location.pathname.startsWith(`/${encodeURIComponent(profilePath)}/links`)}
                            onClick={() => toggleMobileMenu(false)}
                        >
                            Links
                        </NavButton>

                        <NavButton
                            to={`/${profilePath}/wishlists`}
                            variants={itemAnimation}
                            isActive={location.pathname.startsWith(`/${encodeURIComponent(profilePath)}/wishlists`)}
                            onClick={() => toggleMobileMenu(false)}
                        >
                            Wishlists
                        </NavButton>

                        {yatsPortfolio && (
                            <NavButton
                                to={`/${profilePath}/yats`}
                                variants={itemAnimation}
                                isActive={location.pathname.startsWith(`/${encodeURIComponent(profilePath)}/yats`)}
                                onClick={() => toggleMobileMenu(false)}
                            >
                                Yats
                            </NavButton>
                        )}

                        {marketplaceV1Enabled && (
                            <>
                                <NavButton
                                    to={`/${profilePath}/history`}
                                    variants={itemAnimation}
                                    isActive={location.pathname.startsWith(
                                        `/${encodeURIComponent(profilePath)}/history`,
                                    )}
                                    onClick={() => toggleMobileMenu(false)}
                                >
                                    History
                                </NavButton>
                                <NavButton
                                    to={`/${profilePath}/orders`}
                                    variants={itemAnimation}
                                    isActive={location.pathname.startsWith(
                                        `/${encodeURIComponent(profilePath)}/orders`,
                                    )}
                                    onClick={() => toggleMobileMenu(false)}
                                >
                                    Orders
                                </NavButton>
                            </>
                        )}
                    </Buttons>
                </Collapse>
            </Collapseable>
        </>
    );
};
